import React, { useState, useRef, useEffect, useCallback } from 'react'
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop'
import { canvasPreview } from './canvasPreview'
import 'react-image-crop/dist/ReactCrop.css'
import { Button, Tooltip } from '@material-ui/core'
import { useGesture } from '@use-gesture/react'
import Loaders from '../../../custom/components/molecules/Loaders'
import { useTranslation } from 'react-i18next'

// Helper: Center crop with optional aspect
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
    return centerCrop(
        makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight),
        mediaWidth,
        mediaHeight,
    )
}

/**
 * Rotate crop mathematically 90° CW or CCW within a 100×100% coordinate system.
 * Keeps the crop inside bounds and preserves aspect ratio.
 */
function rotateCropBox(crop, direction) {
    if (!crop) return crop

    const { x, y, width, height } = crop
    const newCrop = { ...crop }

    if (direction === 'cw') {
        // Clockwise: swap width and height
        newCrop.x = 100 - y - height
        newCrop.y = x
        newCrop.width = height
        newCrop.height = width
    } else {
        // Counterclockwise
        newCrop.x = y
        newCrop.y = 100 - x - width
        newCrop.width = height
        newCrop.height = width
    }

    // Clamp crop box inside [0,100]
    newCrop.x = Math.max(0, Math.min(100 - newCrop.width, newCrop.x))
    newCrop.y = Math.max(0, Math.min(100 - newCrop.height, newCrop.y))
    return newCrop
}

export default function ImageCropper(props) {
    const { t } = useTranslation()
    const imgRef = useRef(null)
    const canvasRef = useRef(null)
    const containerRef = useRef(null)

    const [crop, setCrop] = useState()
    const [completedCrop, setCompletedCrop] = useState()
    const [rotate, setRotate] = useState(0)
    const [gestureRotate, setGestureRotate] = useState(0)
    const [zoom, setZoom] = useState(1)
    const [aspect, setAspect] = useState(props?.noAspect ? undefined : 16 / 9)
    const [isImageLoading, setIsImageLoading] = useState(true)
    const [imgKey, setImgKey] = useState(null)

    // Reset when new image is loaded
    useEffect(() => {
        setIsImageLoading(true)
        setCrop(undefined)
        setCompletedCrop(undefined)
        setRotate(0)
        setGestureRotate(0)
        setZoom(1)
        setAspect(props?.noAspect ? undefined : 16 / 9)
        if (props.imgSrc) {
            setImgKey(`${props.imgSrc}-${Math.random().toString(36).slice(2, 8)}`)
        }
    }, [props.imgSrc, props.noAspect])

    const onImageLoad = (e) => {
        try {
            const { width, height } = e.currentTarget
            if (aspect) {
                setCrop(centerAspectCrop(width, height, aspect))
            } else {
                setCrop({ unit: '%', x: 0, y: 0, width: 100, height: 100 })
            }
            setIsImageLoading(false)
        } catch (err) {
            console.error('onImageLoad failed:', err)
            setIsImageLoading(false)
        }
    }

    /** ✅ Rotate both image & crop */
    const handleRotate = useCallback(
        (dir) => {
            if (!crop) return
            setRotate((prevRotate) => {
                const newRotate =
                    dir === 'cw' ? (prevRotate + 90) % 360 : (prevRotate - 90 + 360) % 360
                const rotatedCrop = rotateCropBox(crop, dir)
                setCrop(rotatedCrop)
                setCompletedCrop(rotatedCrop)
                return newRotate
            })
        },
        [crop],
    )

    /** ✅ Save final image to blob */
    const onCropSave = async () => {
        try {
            if (!completedCrop || !imgRef.current) return
            const canvas = canvasRef.current || document.createElement('canvas')
            await canvasPreview(imgRef.current, canvas, completedCrop, zoom, rotate + gestureRotate)
            canvas.toBlob(
                (blob) => {
                    if (!blob) return
                    props.getCroppedImage(blob)
                    props.onClose?.()
                },
                'image/jpeg',
                0.9,
            )
        } catch (err) {
            console.error('❌ onCropSave failed:', err)
        }
    }

    /** 👆 Pinch/Rotate gesture handler */
    useGesture(
        {
            onPinch: ({ offset: [d, a] }) => {
                const newZoom = Math.min(Math.max(1 + d / 200, 0.5), 3)
                setZoom(newZoom)
                setGestureRotate(a)
            },
            onWheel: ({ event }) => {
                event.preventDefault()
                setZoom((z) => Math.min(Math.max(z - event.deltaY * 0.001, 0.5), 3))
            },
        },
        {
            target: containerRef,
            eventOptions: { passive: false },
            pinch: { scaleBounds: { min: 0.5, max: 3 }, rubberband: true },
        },
    )

    return (
        <>
            {props.loading ? (
                <div className="noGrid cropLoader">
                    <Loaders isLoading={props.loading} />
                </div>
            ) : (
                <div className="imageCropCnt" ref={containerRef}>
                    <ReactCrop
                        crop={crop}
                        onChange={(_, pcrop) => setCrop(pcrop)}
                        onComplete={(c) => setCompletedCrop(c)}
                        aspect={aspect}
                        key={imgKey}
                    >
                        {props.imgSrc && (
                            <img
                                ref={imgRef}
                                key={imgKey}
                                alt="Crop me"
                                src={props.imgSrc}
                                crossOrigin="anonymous"
                                onLoad={onImageLoad}
                                onError={(e) => {
                                    console.error('❌ Image failed to load:', e)
                                    setIsImageLoading(false)
                                }}
                                style={{
                                    transform: `scale(${zoom}) rotate(${
                                        rotate + gestureRotate
                                    }deg)`,
                                    transformOrigin: 'center center',
                                    maxWidth: '100%',
                                    transition: 'transform 0.15s ease',
                                    display: isImageLoading ? 'none' : 'block',
                                    objectFit: 'contain',
                                    touchAction: 'none', // Prevent double-tap zoom on iOS
                                }}
                            />
                        )}
                    </ReactCrop>

                    {isImageLoading && (
                        <div className="noGrid cropLoader">
                            <Loaders isLoading={isImageLoading} />
                        </div>
                    )}

                    {!isImageLoading && (
                        <div className="cropActions">
                            <Tooltip title="Rotate CCW">
                                <Button className="iconBtn" onClick={() => handleRotate('ccw')}>
                                    <span className="material-icons">rotate_90_degrees_ccw</span>
                                </Button>
                            </Tooltip>
                            <Tooltip title="Rotate CW">
                                <Button className="iconBtn" onClick={() => handleRotate('cw')}>
                                    <span className="material-icons">rotate_90_degrees_cw</span>
                                </Button>
                            </Tooltip>

                            <Tooltip title="Zoom Out">
                                <Button
                                    className="iconBtn"
                                    disabled={zoom <= 0.5}
                                    onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))}
                                >
                                    <span className="material-icons">zoom_out</span>
                                </Button>
                            </Tooltip>
                            <Tooltip title="Zoom In">
                                <Button
                                    className="iconBtn"
                                    disabled={zoom >= 3}
                                    onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
                                >
                                    <span className="material-icons">zoom_in</span>
                                </Button>
                            </Tooltip>

                            <Button className="saveBtn" onClick={onCropSave}>
                                {t('Save Selection')}
                            </Button>
                        </div>
                    )}

                    {/* Hidden canvas for export */}
                    <canvas
                        ref={canvasRef}
                        style={{
                            position: 'absolute',
                            left: '-9999px',
                            width: 1,
                            height: 1,
                        }}
                    />
                </div>
            )}
        </>
    )
}
